import React, {useRef, useImperativeHandle, useEffect, useMemo} from 'react';
import PropTypes from 'prop-types';
import {useDispatch, useSelector} from 'react-redux';
import {getAspirateDispenseVolumeModalData, getDeckLabwareRefs} from 'stores/selectors/virtualTraySelectors';
import {hideAspirateDispenseVolumeModal} from 'stores/actions/virtualTrayAction';
import {
  setTaskTypeAspirateDispenseModal,
  setVolumeAspirateDispenseModal,
} from 'stores/business/aspirateDispenseVolumeModalBusiness';

import {selectOpenedProtocolIsEjectToCaddy} from 'stores/selectors/protocolSelectors';
import {showHighCV} from 'lib/protocolHelper';
import {ColumnLayout} from '../../../components/Layout/Layout';
import TipCapacityStatus from './TipCapacityStatus';
import InputVolume from './InputVolume';
import TipLifetime from './TipLifetime';

import {TaskType, TipLifetimeThreshold} from '../../../constants/dataEnum';

import {useClickOutside, useEnterPress} from '../../../lib/customHooks';
import {mergeRefs} from '../../../lib/refHelper';
import withScrollInput from '../../../lib/withScrollInput';
import HighCV from './HighCV';

const INCREMENT = 0.1;

const AspirateDispenseVolume = React.forwardRef(
  (
    {
      maxTipCapacity,
      bubblePercentage,
      onClose,
      taskType,
      onTaskTypeChange,
      value,
      onChange,
      minInput,
      max,
      increment,
      currentTipVolume,
      taskAspirateAirGapVolume,
      highVolume,
      minTipCapacity,
      recommendedTipCapacity,
    },
    ref,
  ) => {
    const numboxRef = useRef(null);
    const dispatch = useDispatch();

    useEffect(() => {
      numboxRef.current.focus();
    }, []);

    return (
      <div ref={ref} style={{zIndex: '2', filter: 'drop-shadow(0px 4px 4px rgba(0, 0, 0, 0.25))'}}>
        <TipCapacityStatus
          maxCapacity={maxTipCapacity}
          taskAspirateAirGapVolume={taskAspirateAirGapVolume}
          bubblePercentage={bubblePercentage}
          onClose={onClose}
          value={parseFloat(value)}
          currentTipVolume={currentTipVolume}
          taskType={taskType}
          highVolume={highVolume}
          minCapacity={minTipCapacity}
          recommendedCapacity={recommendedTipCapacity}
        />
        <InputVolume
          ref={numboxRef}
          taskType={taskType}
          onTaskTypeChange={onTaskTypeChange}
          value={value}
          onChange={onChange}
          min={minInput}
          max={max}
          increment={increment}
        />
        {((highVolume && value > TipLifetimeThreshold.HIGH_VOLUME_TIP) ||
          (!highVolume && value > TipLifetimeThreshold.NORMAL_TIP)) && <TipLifetime />}
        {dispatch(showHighCV(value)) && taskType !== TaskType.ASPIRATE && taskType !== TaskType.AIR_GAP && (
          <HighCV minCapacity={minTipCapacity} recommendedCapacity={recommendedTipCapacity} taskType={taskType} />
        )}
      </div>
    );
  },
);

AspirateDispenseVolume.propTypes = {
  maxTipCapacity: PropTypes.number,
  minTipCapacity: PropTypes.number,
  recommendedTipCapacity: PropTypes.number,
  bubblePercentage: PropTypes.number,
  onClose: PropTypes.func,
  taskType: PropTypes.string,
  onTaskTypeChange: PropTypes.func,
  value: PropTypes.number,
  onChange: PropTypes.func,
  minInput: PropTypes.number,
  max: PropTypes.number,
  increment: PropTypes.number,
  currentTipVolume: PropTypes.number,
  taskAspirateAirGapVolume: PropTypes.any.isRequired,
  highVolume: PropTypes.bool,
};

AspirateDispenseVolume.defaultProps = {
  maxTipCapacity: 13,
  minTipCapacity: 0.1,
  recommendedTipCapacity: 0.1,
  bubblePercentage: 0,
  onClose: () => {},
  taskType: '',
  onTaskTypeChange: () => {},
  value: 0,
  onChange: () => {},
  minInput: -13,
  max: 13,
  increment: 0.1,
  currentTipVolume: 0,
  highVolume: false,
};

const ScrollableAspirateDispenseVolume = withScrollInput(AspirateDispenseVolume);

const SetAspirateDispenseModal = React.forwardRef(
  (
    {
      maxTipCapacity,
      intl,
      previousSelectedTask,
      excludedComponentRef,
      onApply,
      onChange,
      highVolume,
      minTipCapacity,
      recommendedTipCapacity,
    },
    ref,
  ) => {
    const dispatch = useDispatch();
    const volumeModalData = useSelector(getAspirateDispenseVolumeModalData);
    const deckLabwareRefs = useSelector(getDeckLabwareRefs);
    const isEjectToCaddy = useSelector(selectOpenedProtocolIsEjectToCaddy);

    const {
      initialTipVolume,
      volume,
      maxVolume: maximumVolume,
      minVolume: minimumVolume,
      tipVolumeSequence: volumeByTaskType,
      taskType,
      onApplyCallback,
      position,
    } = volumeModalData;

    // const promiseRef = useRef({});
    const containerRef = useRef(null);

    const deckLabwareRefsArray = useMemo(() => {
      const deckLabwareInArray = Object.values(deckLabwareRefs || {});
      if (isEjectToCaddy) {
        deckLabwareInArray.shift();
      }
      return deckLabwareInArray;
    }, [isEjectToCaddy, deckLabwareRefs]);

    const setVolume = (newVol) => {
      dispatch(setVolumeAspirateDispenseModal(newVol));
    };

    const setTaskType = (type) => {
      dispatch(setTaskTypeAspirateDispenseModal(type));
    };

    useImperativeHandle(ref, () => ({
      hide() {
        hide();
      },
      apply() {
        applyOnEnter();
      },
    }));

    function hide() {
      dispatch(hideAspirateDispenseVolumeModal());
    }

    async function apply() {
      // const { resolve } = promiseRef.current;
      // resolve({ taskType, volume: Math.abs(volume) });
      await onApplyCallback({taskType, volume: Math.abs(volume)});
      previousSelectedTask.current = null;
    }

    function applyOnOutsideClick(clickPosition) {
      if (onApply) onApply(clickPosition);
      apply();
    }

    function applyOnEnter() {
      if (onApply) onApply();
      apply();
    }

    function applyOnClose() {
      if (onApply) onApply();
      apply();
    }

    useClickOutside(containerRef, applyOnOutsideClick, [...excludedComponentRef, ...deckLabwareRefsArray]);

    useEnterPress(containerRef, applyOnEnter);

    function getMinVolumeAllowed() {
      const minVolumeString = parseFloat(minimumVolume).toFixed(1);
      return parseFloat(minVolumeString);
    }

    function getMinVolumeScrollAllowed() {
      let newMinimumVolume = minimumVolume;
      const newVolume = Number(volume);

      if (typeof newVolume === 'number' && volume < minimumVolume) {
        newMinimumVolume = volume;
      }

      const minVolumeString = parseFloat(newMinimumVolume).toFixed(1);
      return parseFloat(minVolumeString);
    }

    function getMaxVolumeAllowed() {
      const maxVolumeString = parseFloat(maximumVolume).toFixed(1);
      return parseFloat(maxVolumeString);
    }

    function onTaskTypeChange(newTaskType) {
      if (taskType === TaskType.MIXING) return;
      if (initialTipVolume !== 0) setTaskType(newTaskType);
      switch (newTaskType) {
        case TaskType.ASPIRATE: {
          let newVolume = 0;
          if (volume < 0) {
            newVolume = (volume * -1).toFixed(1);
          } else {
            newVolume = 0;
          }
          setVolume(newVolume);
          break;
        }
        case TaskType.DISPENSE: {
          let newVolume = 0;
          if (volume <= 0) {
            newVolume = (initialTipVolume * -1).toFixed(1);
          } else {
            newVolume = (volume * -1).toFixed(1);
          }
          setVolume(Math.max(newVolume, getMinVolumeAllowed()));
          break;
        }
        default:
          break;
      }
    }

    function handleVolumeChange(val) {
      if (taskType !== TaskType.AIR_GAP && taskType !== TaskType.MIXING) {
        if (typeof val === 'string' && (val === '' || val[val.length - 1] === '.')) {
          setVolume(val);
          return;
        }

        if (val < 0) {
          setTaskType(TaskType.DISPENSE);
        } else if (val > 0) {
          setTaskType(TaskType.ASPIRATE);
        }
      }

      if (val >= minimumVolume) {
        setVolume(val);
        onChange(val);
      }
    }

    const bubblePercentage = Math.abs(
      taskType === TaskType.ASPIRATE || taskType === TaskType.AIR_GAP || taskType === TaskType.MIXING
        ? 0
        : ((Math.abs(volume) > maxTipCapacity && Math.abs(volume) > initialTipVolume
            ? initialTipVolume
            : parseFloat(volume || 0)) /
            (Math.abs(initialTipVolume) > maxTipCapacity ? initialTipVolume : maxTipCapacity)) *
            100,
    );

    return (
      <ColumnLayout
        ref={mergeRefs(ref, containerRef)}
        position="absolute"
        borderRadius="4px"
        top={`${position.top}px`}
        left={`${position.left}px`}
        overflow="hidden"
        height="fit-content"
        maxWidth="fit-content"
      >
        <ScrollableAspirateDispenseVolume
          taskAspirateAirGapVolume={volumeByTaskType}
          bubblePercentage={bubblePercentage}
          onClose={applyOnClose}
          taskType={taskType}
          onTaskTypeChange={onTaskTypeChange}
          value={volume}
          onChange={handleVolumeChange}
          minInput={getMinVolumeAllowed()}
          min={getMinVolumeScrollAllowed()}
          max={getMaxVolumeAllowed()}
          increment={INCREMENT}
          currentTipVolume={initialTipVolume}
          intl={intl}
          highVolume={highVolume}
          maxTipCapacity={maxTipCapacity}
          minTipCapacity={minTipCapacity}
          recommendedTipCapacity={recommendedTipCapacity}
        />
      </ColumnLayout>
    );
  },
);

SetAspirateDispenseModal.propTypes = {
  maxTipCapacity: PropTypes.number,
  minTipCapacity: PropTypes.number,
  recommendedTipCapacity: PropTypes.number,
  intl: PropTypes.any.isRequired,
  previousSelectedTask: PropTypes.any.isRequired,
  excludedComponentRef: PropTypes.any,
  onApply: PropTypes.any,
  onChange: PropTypes.func,
  highVolume: PropTypes.bool,
};

SetAspirateDispenseModal.defaultProps = {
  maxTipCapacity: 13,
  minTipCapacity: 0.1,
  recommendedTipCapacity: 0.1,
  excludedComponentRef: null,
  onApply: () => {},
  onChange: () => {},
  highVolume: false,
};

function areEqual(prevProps, nextProps) {
  return (
    prevProps.top === nextProps.top &&
    prevProps.left === nextProps.left &&
    prevProps.maxTipCapacity === nextProps.maxCapacity
  );
}

export default React.memo(SetAspirateDispenseModal, areEqual);
